<script lang="ts">
  import { Error } from '@athena-lib/Icons';
  import { generateUniqueElementId } from '@athena-lib/unique-element-id';
  import clsx from 'clsx';
  import Check from '@athena-lib/Icons/Check.svelte';
  import { type TextFieldProps } from './types';

  let {
    id: providedId,
    class: className,
    value = $bindable(),
    label,
    inputElement = $bindable(),
    error,
    helpText,
    successText,
    helperElement,
    helperElementClass,
    adornment,
    ...restProps
  }: TextFieldProps = $props();

  const id = $derived(providedId ?? generateUniqueElementId());
  const { messageColor, message, Icon, borderClass } = $derived.by(() => {
    if (error) {
      return {
        messageColor: clsx('text-st-red-500'),
        message: error,
        Icon: Error,
        borderClass: clsx('border border-2 border-st-red-500'),
      };
    }
    if (successText) {
      return {
        messageColor: clsx('text-st-green-500'),
        message: successText,
        Icon: Check,
        borderClass: clsx('border-st-green-500 ring ring-st-green-200'),
      };
    }
    if (helpText) {
      return { message: helpText, messageColor: clsx('text-st-gray-800') };
    }
    return {};
  });
</script>

<div class={clsx(className, 'flex flex-col')}>
  <div class="flex flex-row">
    {#if adornment}
      <div
        class={clsx(
          'flex',
          'h-12',
          'rounded',
          'border',
          'border-r-0',
          'rounded-r-none',
          'border-st-gray-500',
          'bg-white',
          'px-4',
          'pt-3',
        )}
      >
        <div>{adornment}</div>
      </div>
    {/if}
    <div
      class={clsx(
        'w-full',
        'relative',
        'flex',
        label ? 'h-16' : 'h-10',
        'rounded',
        'border',
        'border-st-gray-300',
        'bg-white',
        'shadow-sm',
        adornment ? 'rounded-l-none' : undefined,
        'px-4',
        label ? 'pt-4' : '',
        'has-[:disabled]:border-st-gray-400 has-[:disabled]:bg-st-gray-100',
        borderClass
          ? borderClass
          : 'has-[:focus]:border-st-blue-500 has-[:focus]:ring has-[:focus]:ring-st-blue-100',
      )}
    >
      <input
        {...restProps}
        {id}
        bind:this={inputElement}
        bind:value
        class="peer w-full text-base text-st-gray-900 placeholder-transparent outline-none disabled:text-st-gray-500"
        aria-invalid={!!error}
        aria-describedby={message ? `${id}-message` : undefined}
      />
      {#if label}
        <label
          for={id}
          class={clsx(
            'pointer-events-none absolute transition-all peer-disabled:text-st-gray-600',
            value?.length > 0
              ? 'top-1 text-sm text-gray-500'
              : 'top-5 text-base text-gray-900 peer-focus:top-1 peer-focus:text-sm peer-focus:text-gray-500',
          )}
          >{#if !label || typeof label === 'string'}{label}{:else}{@render label()}{/if}</label
        >
      {/if}
      {#if Icon}
        <div
          class={clsx('absolute right-2.5 top-2.5 text-st-green-500', messageColor)}
          aria-hidden="true"
        >
          <Icon sizeClass="h-5 w-5" />
        </div>
      {/if}
    </div>
  </div>
  {#if helperElement}
    <div class={clsx('relative w-full', helperElementClass)}>
      {@render helperElement()}
    </div>
  {/if}
  {#if message}
    <span id={`${id}-message`} class={clsx('mt-1', 'text-sm', 'font-normal', messageColor)}>
      {message}
    </span>
  {/if}
</div>
